import React, { useState } from 'react';
import {
  ICalcomEventType,
  ICreatePropertyBookingLinkRequest,
  IPropertyBookingLink,
  IUpdatePropertyBookingLinkRequest,
  IUserAssociatedProperty,
  JuniperEventType,
} from '../../types';
import ManageBookingLinksDialog from '../ManageBookingLinksDialog/ManageBookingLinksDialog';
import './booking-link-property-card.scss';

interface IManagedKind {
  kind: JuniperEventType;
  label: string;
}

const MANAGED_KINDS: IManagedKind[] = [
  { kind: JuniperEventType.Walkthrough, label: 'Walkthrough' },
  { kind: JuniperEventType.KeyPickup, label: 'Key pickup' },
];

interface BookingLinkPropertyCardProps {
  property: IUserAssociatedProperty;
  eventTypes: ICalcomEventType[];
  linksFor: (
    propertyId: string,
    kind: JuniperEventType
  ) => IPropertyBookingLink[];
  isSubmitting: boolean;
  onCreate: (request: ICreatePropertyBookingLinkRequest) => Promise<boolean>;
  onUpdate: (
    bookingLinkId: string,
    request: IUpdatePropertyBookingLinkRequest
  ) => Promise<boolean>;
  onRemove: (bookingLinkId: string) => Promise<boolean>;
  deepLinkPropertyId?: string;
  deepLinkKind?: JuniperEventType;
}

interface IKindRow {
  kind: JuniperEventType;
  label: string;
  links: IPropertyBookingLink[];
}

const BookingLinkPropertyCard = ({
  property,
  eventTypes,
  linksFor,
  isSubmitting,
  onCreate,
  onUpdate,
  onRemove,
  deepLinkPropertyId,
  deepLinkKind,
}: BookingLinkPropertyCardProps) => {
  const isDeepLinked: boolean = deepLinkPropertyId === property.propertyId;

  const [openKind, setOpenKind] = useState<JuniperEventType | null>(
    isDeepLinked ? deepLinkKind ?? null : null
  );

  const kindRows: IKindRow[] = MANAGED_KINDS.map(
    (managedKind: IManagedKind) => ({
      kind: managedKind.kind,
      label: managedKind.label,
      links: linksFor(property.propertyId, managedKind.kind),
    })
  );

  const openRow: IKindRow | undefined = kindRows.find(
    (row: IKindRow) => row.kind === openKind
  );

  return (
    <div className="booking-link-property-card">
      <h5 className="property-name">{property.name}</h5>

      <ul className="kind-row-list">
        {kindRows.map((row: IKindRow) => {
          const hasLinks: boolean = row.links.length > 0;

          return (
            <li key={row.kind} className="kind-row">
              <span className="kind-row-label">{row.label}</span>

              {hasLinks ? (
                <span className="kind-row-trailing">
                  <span className="kind-row-status">
                    <span className="kind-row-status-dot" aria-hidden="true" />
                    Active
                  </span>
                  <button
                    type="button"
                    className="kind-row-action"
                    onClick={() => setOpenKind(row.kind)}
                  >
                    Manage
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  className="kind-row-action"
                  onClick={() => setOpenKind(row.kind)}
                >
                  Set up
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {openRow && (
        <ManageBookingLinksDialog
          propertyId={property.propertyId}
          propertyName={property.name}
          kind={openRow.kind}
          kindLabel={openRow.label}
          eventTypes={eventTypes}
          links={openRow.links}
          isSubmitting={isSubmitting}
          onCreate={onCreate}
          onUpdate={onUpdate}
          onRemove={onRemove}
          onClose={() => setOpenKind(null)}
          autoStartAdd={
            isDeepLinked &&
            openRow.kind === deepLinkKind &&
            openRow.links.length === 0
          }
        />
      )}
    </div>
  );
};

export default BookingLinkPropertyCard;